import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo, useEffect, useState} from 'react';

import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {MAX_TASK_SCHEMA_NAME_LENGTH} from 'processes/settings/fields/ProcessTaskSchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Breadcrumb from 'ui-library/Breadcrumb';
import useHoverState from 'ui-library/hooks/useHoverState';
import usePrevious from 'ui-library/hooks/usePrevious';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import DoubleClickEditableTextField, {
  EmptyStringCompletionPolicy,
} from 'ui-library/reactflow/nodes/DoubleClickEditableTextField';
import {SaveState, SaveStatusIndicator} from 'ui-library/SaveStatusIndicator';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  ProcessFlowchartConfigWithValidSchemaGQL,
  StaticTemplateFlowchart,
} from './processFlowchartConfigEditorConstants';
import {
  HeaderConfigAdditionalProps,
  ProcessFlowchartConfigEditorHeaderActionsProps,
  ProcessFlowchartConfigEditorHeaderConfig,
} from './ProcessFlowchartConfigEditorHeaderTypes';

export interface ProcessFlowchartConfigEditorHeaderProps {
  headerConfig: ProcessFlowchartConfigEditorHeaderConfig;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartConfig: ProcessFlowchartConfigWithValidSchemaGQL;
  saveState: SaveState | null;
  hasUnsavedChanges: boolean;
  isAutosaverPaused: boolean;
  customSaveStatusMessage?: string;
  locked: boolean;
  validationErrors: Array<ProcessFlowchartValidationError>;
  staticFlowchart: StaticTemplateFlowchart;
  /** Optional metadata and callbacks to override header config values dynamically. */
  headerConfigAdditionalProps?: HeaderConfigAdditionalProps;
}

function TitleViewer(props: {displayName: string; isEditingName: boolean}): JSX.Element {
  const {displayName, isEditingName} = props;
  const [isHovered, hoverProps, clearHoverState] = useHoverState();
  const previousIsEditingName = usePrevious(isEditingName);

  useEffect(() => {
    // Clear hover state when exiting edit mode to prevent hover styling from persisting when the element
    // reappears, since a blur event will not trigger onMouseLeave for this component.
    if (previousIsEditingName && !isEditingName) {
      clearHoverState();
    }
  }, [isEditingName, previousIsEditingName, clearHoverState]);

  return (
    <div className={classNames(css(styles.name), isHovered && css(styles.onHoverName))} {...hoverProps}>
      {displayName}
    </div>
  );
}

export default function ProcessFlowchartConfigEditorHeader(
  props: ProcessFlowchartConfigEditorHeaderProps
): JSX.Element {
  const {headerConfig, headerConfigAdditionalProps} = props;
  const displayName = headerConfigAdditionalProps?.flowchartNameOverride ?? props.rootTaskSchema.name;
  const ActionsComponent = headerConfig.actionsComponent;
  const HeaderLeftContent = headerConfig.headerLeftContent;

  const breadcrumbItems = useMemo(
    () => headerConfigAdditionalProps?.dynamicBreadcrumbs ?? headerConfig.breadcrumbItems,
    [headerConfig.breadcrumbItems, headerConfigAdditionalProps?.dynamicBreadcrumbs]
  );

  const headerPropsForComponents: ProcessFlowchartConfigEditorHeaderActionsProps = {
    flowchartConfig: props.flowchartConfig,
    rootTaskSchema: props.rootTaskSchema,
    saveState: props.saveState,
    locked: props.locked,
    hasUnsavedChanges: props.hasUnsavedChanges,
    isAutosaverPaused: props.isAutosaverPaused,
    validationErrors: props.validationErrors,
    staticFlowchart: props.staticFlowchart,
  };

  // Prevent showing save indicator on initial load
  const previousSaveState = usePrevious<SaveState | null>(props.saveState);
  const [isEditingName, setIsEditingName] = useState(false);

  const shouldShowSaveStatusIndicator = useMemo((): boolean => {
    if (props.locked) {
      return false;
    } else if (previousSaveState == null) {
      return false;
    } else if (props.saveState == null) {
      return false;
    } else if (previousSaveState === SaveState.SAVED && props.saveState === SaveState.SAVED) {
      // Can take a few render cycles for initial load status to settle, so prevent showing
      // saved indicator if they are both SAVED.
      // Note, "saved" shows if not dirty, not locked, not in flight -> so without this check,
      // we would show "saved" on load
      return false;
    }
    return true;
  }, [props.locked, previousSaveState, props.saveState]);

  const handleRename = async (updatedName: string): Promise<void> => {
    if (updatedName === displayName) {
      return;
    }
    await props.headerConfigAdditionalProps?.onRename?.(updatedName);
  };

  return (
    <div className={css(styles.headerBar)}>
      <div className={css(styles.headerLeft)}>
        <BenchlingIcon
          icon={headerConfigAdditionalProps?.dynamicIcon ?? headerConfig.icon}
          className={css(styles.headerIcon)}
        />

        <div className={css(styles.titleGroup)}>
          <Breadcrumb items={breadcrumbItems} isLastItemInteractable={true} />

          <div
            className={css(styles.titleRow)}
            {...getTestProps({component: 'ProcessFlowchartConfigEditorHeader', element: 'title'})}
          >
            {props.headerConfigAdditionalProps?.onRename != null && !props.locked ? (
              <DoubleClickEditableTextField
                onDraftNameChanged={noop} // This can be implemented for validation
                emptyStringCompletionPolicy={EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE}
                defaultValue={() => displayName}
                isEditing={isEditingName}
                setIsEditing={setIsEditingName}
                onComplete={handleRename}
                maxValueLength={MAX_TASK_SCHEMA_NAME_LENGTH}
                value={displayName}
                viewer={<TitleViewer displayName={displayName} isEditingName={isEditingName} />}
              />
            ) : (
              <div className={css(styles.name)}>{displayName}</div>
            )}

            {props.locked && <BenchlingIcon icon="lock" className={css(styles.lockIcon)} />}
          </div>
        </div>

        {HeaderLeftContent && <HeaderLeftContent {...headerPropsForComponents} />}

        {shouldShowSaveStatusIndicator && (
          <SaveStatusIndicator
            saveStatus={props.saveState!}
            messageOverride={props.customSaveStatusMessage}
            dataTestAttrs={{
              component: 'ProcessFlowchartConfigEditorHeader',
              element: 'SaveStateIndicator',
            }}
          />
        )}
      </div>

      <div className={css(styles.headerRight)}>
        {ActionsComponent && (
          <div className={css(styles.buttonContainer)}>
            <ActionsComponent {...headerPropsForComponents} />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px 8px 16px',
    borderBottom: `solid ${Color.GRAY_200} 1px `,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerIcon: {
    fontSize: '20px',
  },
  lockIcon: {
    fontSize: '17px',
  },
  titleGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  name: {
    fontSize: '17px',
  },
  onHoverName: {
    backgroundColor: Color.GRAY_200,
    cursor: 'text',
  },
  buttonContainer: {
    marginLeft: '15px',
  },
});
